import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { API_URL } from '../../api/client';
import { adminApi } from '../../api';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';
import './Customer360.css';

export const Customer360: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  // Notes state
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isPinned, setIsPinned] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Flags state
  const [showFlagForm, setShowFlagForm] = useState(false);
  const [flagType, setFlagType] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [savingFlag, setSavingFlag] = useState(false);

  // Suspend/unsuspend state
  const [actionLoading, setActionLoading] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/admin/applications');
      return;
    }

    fetchCustomer360();
  }, [userId]);

  const fetchCustomer360 = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/customers/${userId}/360`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();

      if (data.success && data.data) {
        setCustomer(data.data.customer);
      } else {
        setError(data.message || 'Failed to load customer data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!noteContent.trim()) {
      alert('Please enter note content');
      return;
    }

    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/admin/customers/${userId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          note_content: noteContent,
          note_type: noteType,
          is_pinned: isPinned,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh customer data
        await fetchCustomer360();
        setShowNoteForm(false);
        setNoteContent('');
        setNoteType('general');
        setIsPinned(false);
      } else {
        setError(data.message || 'Failed to create note');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/customers/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        await fetchCustomer360();
      } else {
        setError(data.message || 'Failed to delete note');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    }
  };

  const handleAddFlag = async () => {
    if (!flagType) {
      alert('Please select a flag type');
      return;
    }

    setSavingFlag(true);
    try {
      const response = await fetch(`${API_URL}/admin/customers/${userId}/flags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          flag_type: flagType,
          flag_reason: flagReason,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCustomer360();
        setShowFlagForm(false);
        setFlagType('');
        setFlagReason('');
      } else {
        setError(data.message || 'Failed to add flag');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add flag');
    } finally {
      setSavingFlag(false);
    }
  };

  const handleRemoveFlag = async (flagId: string) => {
    if (!window.confirm('Are you sure you want to remove this flag?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/customers/flags/${flagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        await fetchCustomer360();
      } else {
        setError(data.message || 'Failed to remove flag');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove flag');
    }
  };

  const handleSuspendCustomer = () => {
    setShowSuspendModal(true);
  };

  const confirmSuspendCustomer = async () => {
    if (!suspendReason.trim()) {
      alert('Please provide a reason for suspension');
      return;
    }

    try {
      setActionLoading(true);
      const response = await adminApi.suspendCustomer(userId!, suspendReason);

      if (response.success) {
        alert('Customer suspended successfully');
        await fetchCustomer360(); // Refresh customer data
        setShowSuspendModal(false);
        setSuspendReason('');
      } else {
        alert(response.message || 'Failed to suspend customer');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to suspend customer');
    } finally {
      setActionLoading(false);
    }
  };

  const cancelSuspend = () => {
    setShowSuspendModal(false);
    setSuspendReason('');
  };

  const handleUnsuspendCustomer = async () => {
    if (!window.confirm(
      `Are you sure you want to unsuspend ${customer.profile.first_name} ${customer.profile.last_name}?`
    )) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await adminApi.unsuspendCustomer(userId!);

      if (response.success) {
        alert('Customer unsuspended successfully');
        await fetchCustomer360(); // Refresh customer data
      } else {
        alert(response.message || 'Failed to unsuspend customer');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to unsuspend customer');
    } finally {
      setActionLoading(false);
    }
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'warning': return 'note-warning';
      case 'positive': return 'note-positive';
      case 'follow_up': return 'note-follow-up';
      default: return 'note-general';
    }
  };

  const getFlagTypeColor = (type: string) => {
    switch (type) {
      case 'high_risk': return 'flag-danger';
      case 'vip': return 'flag-success';
      case 'payment_issues': return 'flag-warning';
      case 'good_payer': return 'flag-info';
      default: return 'flag-secondary';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!customer) {
    return (
      <div className="error-state">
        <p>Customer not found</p>
      </div>
    );
  }

  const getInitials = () => {
    const first = customer.profile.first_name?.charAt(0) || '';
    const last = customer.profile.last_name?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  return (
    <div className="customer360-container">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {/* Modern Header with Selfie Photo */}
      <div className="customer360-header">
        <div className="customer360-header-content">
          <div className="customer360-photo-section">
            {customer.profile.selfie_photo ? (
              <img
                src={customer.profile.selfie_photo}
                alt={`${customer.profile.first_name} ${customer.profile.last_name}`}
                className="customer360-photo"
              />
            ) : (
              <div className="customer360-photo-placeholder">
                {getInitials()}
              </div>
            )}
          </div>

          <div className="customer360-info">
            <h1 className="customer360-name">
              {customer.profile.first_name} {customer.profile.middle_name} {customer.profile.last_name}
            </h1>

            <div className="customer360-meta">
              <div className="customer360-meta-item">
                <span className="customer360-meta-label">Customer ID</span>
                <span className="customer360-meta-value">{customer.profile.user_id.slice(0, 12)}...</span>
              </div>
              <div className="customer360-meta-item">
                <span className="customer360-meta-label">Status</span>
                <span className="customer360-meta-value">
                  <span className={`customer360-status-badge ${customer.profile.status}`}>
                    {customer.profile.status}
                  </span>
                </span>
              </div>
              <div className="customer360-meta-item">
                <span className="customer360-meta-label">Member Since</span>
                <span className="customer360-meta-value">
                  {new Date(customer.profile.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="customer360-meta-item">
                <span className="customer360-meta-label">Email</span>
                <span className="customer360-meta-value">{customer.profile.email}</span>
              </div>
              <div className="customer360-meta-item">
                <span className="customer360-meta-label">Phone</span>
                <span className="customer360-meta-value">{customer.profile.phone}</span>
              </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <Button
                onClick={() => navigate('/admin/customers')}
                variant="secondary"
                icon={<ArrowLeft size={16} />}
              >
                Back to Customers
              </Button>

              {customer.profile.status === 'active' ? (
                <Button
                  onClick={handleSuspendCustomer}
                  disabled={actionLoading}
                  variant="danger"
                  loading={actionLoading}
                >
                  Suspend Customer
                </Button>
              ) : customer.profile.status === 'suspended' ? (
                <Button
                  onClick={handleUnsuspendCustomer}
                  disabled={actionLoading}
                  variant="primary"
                  loading={actionLoading}
                >
                  Unsuspend Customer
                </Button>
              ) : null}
            </div>

            {customer.flags && customer.flags.length > 0 && (
              <div className="customer360-flags">
                {customer.flags.map((flag: any) => (
                  <span key={flag.flag_id} className="customer360-flag">
                    {flag.flag_type.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="customer360-stats">
        <div className="customer360-stat-card">
          <div className="customer360-stat-label">Total Loans</div>
          <div className="customer360-stat-value">{customer.loans.length}</div>
        </div>
        <div className="customer360-stat-card">
          <div className="customer360-stat-label">Total Applications</div>
          <div className="customer360-stat-value">{customer.applications.length}</div>
        </div>
        <div className="customer360-stat-card">
          <div className="customer360-stat-label">Total Paid</div>
          <div className="customer360-stat-value success">
            {formatCurrency(customer.payment_behavior.total_amount_paid)}
          </div>
        </div>
        <div className="customer360-stat-card">
          <div className="customer360-stat-label">Payment Success Rate</div>
          <div className="customer360-stat-value success">
            {customer.payment_behavior.payment_success_rate.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="customer360-tabs">
        <div className="customer360-tabs-list">
          <button
            className={`customer360-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`customer360-tab ${activeTab === 'loans' ? 'active' : ''}`}
            onClick={() => setActiveTab('loans')}
          >
            Loans ({customer.loans.length})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'applications' ? 'active' : ''}`}
            onClick={() => setActiveTab('applications')}
          >
            Applications ({customer.applications.length})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments ({customer.payments.length})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents ({customer.documents.length})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'communications' ? 'active' : ''}`}
            onClick={() => setActiveTab('communications')}
          >
            Communications ({customer.notifications.length})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes ({customer.notes ? customer.notes.length : 0})
          </button>
          <button
            className={`customer360-tab ${activeTab === 'flags' ? 'active' : ''}`}
            onClick={() => setActiveTab('flags')}
          >
            Flags ({customer.flags ? customer.flags.length : 0})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="customer360-content">
        {activeTab === 'profile' && (
          <div className="customer360-tab-panel">
            <div className="customer360-profile-grid">
              <div className="customer360-profile-section">
                <h3>Personal Information</h3>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Email</span>
                  <span className="customer360-profile-field-value">{customer.profile.email}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Phone</span>
                  <span className="customer360-profile-field-value">{customer.profile.phone}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">BVN</span>
                  <span className="customer360-profile-field-value">{customer.profile.bvn || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Date of Birth</span>
                  <span className="customer360-profile-field-value">
                    {customer.profile.date_of_birth
                      ? new Date(customer.profile.date_of_birth).toLocaleDateString()
                      : 'Not provided'}
                  </span>
                </div>
              </div>

              <div className="customer360-profile-section">
                <h3>Address Information</h3>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Address</span>
                  <span className="customer360-profile-field-value">{customer.profile.address || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">City</span>
                  <span className="customer360-profile-field-value">{customer.profile.city || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">State</span>
                  <span className="customer360-profile-field-value">{customer.profile.state || 'Not provided'}</span>
                </div>
              </div>

              <div className="customer360-profile-section">
                <h3>Employment Details</h3>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Employer</span>
                  <span className="customer360-profile-field-value">{customer.profile.employer || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Occupation</span>
                  <span className="customer360-profile-field-value">{customer.profile.occupation || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Monthly Income</span>
                  <span className="customer360-profile-field-value">
                    {customer.profile.monthly_income
                      ? formatCurrency(customer.profile.monthly_income)
                      : 'Not provided'}
                  </span>
                </div>
              </div>

              <div className="customer360-profile-section">
                <h3>Banking Information</h3>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Bank Name</span>
                  <span className="customer360-profile-field-value">{customer.profile.bank_name || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Account Number</span>
                  <span className="customer360-profile-field-value">{customer.profile.account_number || 'Not provided'}</span>
                </div>
                <div className="customer360-profile-field">
                  <span className="customer360-profile-field-label">Account Name</span>
                  <span className="customer360-profile-field-value">{customer.profile.account_name || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'loans' && (
          <div className="customer360-tab-panel">
            {customer.loans.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">📋</div>
                <div className="customer360-empty-text">No loans found for this customer</div>
              </div>
            ) : (
              <div className="customer360-table-wrapper">
                <table className="customer360-table">
                  <thead>
                    <tr>
                      <th>Loan Reference</th>
                      <th>Principal</th>
                      <th>Interest Rate</th>
                      <th>Term</th>
                      <th>Outstanding</th>
                      <th>Total Paid</th>
                      <th>Status</th>
                      <th>Disbursed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.loans.map((loan: any) => (
                      <tr key={loan.loan_id}>
                        <td>{loan.loan_reference}</td>
                        <td>{formatCurrency(loan.principal_amount)}</td>
                        <td>{parseFloat(loan.interest_rate).toFixed(2)}%</td>
                        <td>{loan.loan_term_months} months</td>
                        <td>
                          {formatCurrency(parseFloat(loan.outstanding_principal) + parseFloat(loan.outstanding_interest))}
                        </td>
                        <td>{formatCurrency(loan.total_paid)}</td>
                        <td>
                          <span className={`customer360-status-badge ${loan.status}`}>{loan.status}</span>
                        </td>
                        <td>{new Date(loan.disbursement_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="customer360-tab-panel">
            {customer.applications.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">📝</div>
                <div className="customer360-empty-text">No applications found for this customer</div>
              </div>
            ) : (
              <div className="customer360-table-wrapper">
                <table className="customer360-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Term</th>
                      <th>Status</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.applications.map((app: any) => (
                      <tr key={app.application_id}>
                        <td>{app.application_reference}</td>
                        <td>{app.product_name}</td>
                        <td>{formatCurrency(app.amount_requested)}</td>
                        <td>{app.loan_term_months} months</td>
                        <td>
                          <span className={`customer360-status-badge ${app.status}`}>{app.status}</span>
                        </td>
                        <td>
                          {app.submitted_at
                            ? new Date(app.submitted_at).toLocaleDateString()
                            : 'Not submitted'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="customer360-tab-panel">
            {customer.payments.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">💳</div>
                <div className="customer360-empty-text">No payments found for this customer</div>
              </div>
            ) : (
              <div className="customer360-table-wrapper">
                <table className="customer360-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Loan</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.payments.map((payment: any) => (
                      <tr key={payment.payment_id}>
                        <td>{payment.payment_reference}</td>
                        <td>{payment.loan_reference}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>{payment.payment_method}</td>
                        <td>
                          <span className={`customer360-status-badge ${payment.status}`}>{payment.status}</span>
                        </td>
                        <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                        <td>
                          {payment.status === 'successful' && (
                            <Button
                              onClick={() => window.open(`${API_URL}/payments/${payment.payment_id}/receipt`, '_blank')}
                              variant="secondary"
                              size="sm"
                            >
                              Download
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="customer360-tab-panel">
            {customer.documents.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">📄</div>
                <div className="customer360-empty-text">No documents uploaded yet</div>
              </div>
            ) : (
              <div className="customer360-table-wrapper">
                <table className="customer360-table">
                  <thead>
                    <tr>
                      <th>Document Type</th>
                      <th>File Name</th>
                      <th>Verified</th>
                      <th>Uploaded</th>
                      <th>Verified At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.documents.map((doc: any) => (
                      <tr key={doc.document_id}>
                        <td>{doc.document_type}</td>
                        <td>{doc.file_name}</td>
                        <td>
                          <span className={`customer360-status-badge ${doc.is_verified ? 'successful' : 'pending'}`}>
                            {doc.is_verified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                        <td>
                          {doc.verified_at ? new Date(doc.verified_at).toLocaleDateString() : 'Not verified'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'communications' && (
          <div className="customer360-tab-panel">
            {customer.notifications.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">📨</div>
                <div className="customer360-empty-text">No communications sent to this customer</div>
              </div>
            ) : (
              <div className="customer360-table-wrapper">
                <table className="customer360-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Channel</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.notifications.map((notif: any) => (
                      <tr key={notif.notification_id}>
                        <td>{notif.notification_type}</td>
                        <td>{notif.channel}</td>
                        <td>{notif.subject}</td>
                        <td>
                          <span className={`customer360-status-badge ${notif.status}`}>{notif.status}</span>
                        </td>
                        <td>{new Date(notif.created_at).toLocaleDateString()}</td>
                        <td>
                          {notif.sent_at ? new Date(notif.sent_at).toLocaleDateString() : 'Not sent'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="customer360-tab-panel">
            <div className="customer360-action-bar">
              <h3>Customer Notes</h3>
              {!showNoteForm && (
                <Button onClick={() => setShowNoteForm(true)} variant="primary">
                  Add Note
                </Button>
              )}
            </div>

            {showNoteForm && (
              <div className="customer360-note-card">
                <h4>Add New Note</h4>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 500}}>Note Type:</label>
                  <select value={noteType} onChange={(e) => setNoteType(e.target.value)} style={{width: '100%', padding: '0.625rem', borderRadius: '6px', border: '1px solid #d1d5db'}}>
                    <option value="general">General</option>
                    <option value="warning">Warning</option>
                    <option value="positive">Positive</option>
                    <option value="follow_up">Follow Up</option>
                  </select>
                </div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 500}}>Note Content:</label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    rows={4}
                    placeholder="Enter note content..."
                    style={{width: '100%', padding: '0.625rem', borderRadius: '6px', border: '1px solid #d1d5db'}}
                  />
                </div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                    />
                    Pin this note (appears at top)
                  </label>
                </div>
                <div style={{display: 'flex', gap: '1rem'}}>
                  <Button
                    onClick={handleCreateNote}
                    disabled={savingNote}
                    variant="primary"
                    loading={savingNote}
                  >
                    Save Note
                  </Button>
                  <Button
                    onClick={() => {
                      setShowNoteForm(false);
                      setNoteContent('');
                      setNoteType('general');
                      setIsPinned(false);
                    }}
                    disabled={savingNote}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!customer.notes || customer.notes.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">📝</div>
                <div className="customer360-empty-text">No notes yet. Add the first note to track important information about this customer.</div>
              </div>
            ) : (
              customer.notes.map((note: any) => (
                <div key={note.note_id} className={`customer360-note-card ${note.is_pinned ? 'pinned' : ''}`}>
                  <div className="customer360-note-header">
                    <span className="customer360-note-type">{note.note_type.replace('_', ' ')}</span>
                    {note.is_pinned && <span>📌 Pinned</span>}
                  </div>
                  <div className="customer360-note-content">{note.note_content}</div>
                  <div className="customer360-note-meta">
                    <span>{note.created_by_name}</span>
                    <span>{new Date(note.created_at).toLocaleDateString()}</span>
                    <Button
                      onClick={() => handleDeleteNote(note.note_id)}
                      variant="danger"
                      size="sm"
                      style={{marginLeft: 'auto'}}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'flags' && (
          <div className="customer360-tab-panel">
            <div className="customer360-action-bar">
              <h3>Customer Flags</h3>
              {!showFlagForm && (
                <Button onClick={() => setShowFlagForm(true)} variant="primary">
                  Add Flag
                </Button>
              )}
            </div>

            {showFlagForm && (
              <div className="customer360-flag-card">
                <h4>Add New Flag</h4>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 500}}>Flag Type:</label>
                  <select value={flagType} onChange={(e) => setFlagType(e.target.value)} style={{width: '100%', padding: '0.625rem', borderRadius: '6px', border: '1px solid #d1d5db'}}>
                    <option value="">Select flag type...</option>
                    <option value="high_risk">High Risk</option>
                    <option value="vip">VIP Customer</option>
                    <option value="payment_issues">Payment Issues</option>
                    <option value="good_payer">Good Payer</option>
                    <option value="requires_follow_up">Requires Follow-up</option>
                    <option value="fraud_alert">Fraud Alert</option>
                    <option value="blacklisted">Blacklisted</option>
                  </select>
                </div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 500}}>Reason (optional):</label>
                  <textarea
                    value={flagReason}
                    onChange={(e) => setFlagReason(e.target.value)}
                    rows={3}
                    placeholder="Enter reason for this flag..."
                    style={{width: '100%', padding: '0.625rem', borderRadius: '6px', border: '1px solid #d1d5db'}}
                  />
                </div>
                <div style={{display: 'flex', gap: '1rem'}}>
                  <Button
                    onClick={handleAddFlag}
                    disabled={savingFlag}
                    variant="primary"
                    loading={savingFlag}
                  >
                    Add Flag
                  </Button>
                  <Button
                    onClick={() => {
                      setShowFlagForm(false);
                      setFlagType('');
                      setFlagReason('');
                    }}
                    disabled={savingFlag}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!customer.flags || customer.flags.length === 0 ? (
              <div className="customer360-empty">
                <div className="customer360-empty-icon">🏴</div>
                <div className="customer360-empty-text">No flags set. Add flags to quickly identify customer characteristics.</div>
              </div>
            ) : (
              customer.flags.map((flag: any) => (
                <div key={flag.flag_id} className="customer360-flag-card">
                  <div className="customer360-flag-header">
                    <span className="customer360-flag-type">{flag.flag_type.replace(/_/g, ' ')}</span>
                    <Button
                      onClick={() => handleRemoveFlag(flag.flag_id)}
                      variant="danger"
                      size="sm"
                    >
                      Remove
                    </Button>
                  </div>
                  {flag.flag_reason && (
                    <div className="customer360-flag-reason">{flag.flag_reason}</div>
                  )}
                  <div className="customer360-flag-meta">
                    <span>Added by {flag.added_by_name}</span>
                    <span>{new Date(flag.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Suspend Customer Modal */}
      {showSuspendModal && (
        <div className="modal-overlay" onClick={cancelSuspend}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Suspend Customer</h2>
              <button className="modal-close" onClick={cancelSuspend}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-customer-name">
                {customer.profile.first_name} {customer.profile.middle_name} {customer.profile.last_name}
              </p>
              <p className="modal-warning">
                ⚠️ This customer will be unable to login and will be notified to contact support.
              </p>
              <div className="form-group">
                <label htmlFor="suspendReason">Reason for Suspension *</label>
                <textarea
                  id="suspendReason"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter the reason for suspending this customer..."
                  rows={4}
                  className="form-textarea"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button
                onClick={cancelSuspend}
                variant="secondary"
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSuspendCustomer}
                variant="danger"
                disabled={actionLoading || !suspendReason.trim()}
                loading={actionLoading}
              >
                Confirm Suspension
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
